import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DEFAULT_LOCALE, RTL_LOCALES } from "./constants";
import { dictionaries } from "./dictionaries";
import { getByPath } from "./translate";

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
  dir: "ltr",
});

// The pre-body blocking script (see gatsby-ssr.js) only prevents a flash of
// the wrong dir/lang on repeat visits by reading localStorage before paint.
// State must start at DEFAULT_LOCALE (matching the server-rendered markup)
// rather than reading window.__locale synchronously, or a returning Farsi
// visitor's first client render would mismatch the English SSR output — this
// effect corrects it right after mount instead. Toggling later goes straight
// through setLocale below, which drives React state directly rather than
// bouncing through a global callback the script would otherwise need to own.
export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);

  useEffect(() => {
    if (window.__locale && window.__locale !== DEFAULT_LOCALE) {
      setLocaleState(window.__locale);
    }
  }, []);

  const setLocale = useCallback((next) => {
    setLocaleState(next);
    const html = document.documentElement;
    html.lang = next;
    html.dir = RTL_LOCALES.includes(next) ? "rtl" : "ltr";
    window.__locale = next;
    try {
      localStorage.setItem("locale", next);
    } catch (e) {}
  }, []);

  const dict = dictionaries[locale] || dictionaries[DEFAULT_LOCALE];
  const t = useCallback((key, vars) => getByPath(dict, key, vars), [dict]);
  const dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t, dir }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => useContext(LocaleContext);
